import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState} from "react";
import {Controller, useForm} from "react-hook-form";
import {useNavigate} from "react-router-dom"
import {Drawer, Grid, MenuItem, Paper, Select, Stack} from "@mui/material";
import {LocationEndpoints} from "@/services/location/endpoints"
import GenericService from "@/services/GenericService";
import {object, string, TypeOf, z} from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import ControlAutoComplete from "@/components/hook-form/ControlAutoComplete";
import ControlledTextFiled from "@/components/ngteco-hook-form/ControlledTextFiled";
import {ngteco_timezones} from "@/components/resource/timezone";
import SnackbarUtil from "@/utils/SnackbarUtil";
import {CancelButton, SavingButton} from "@/components/data-grid/CustomButton";
import {useTranslation} from "react-i18next";
import NGInputLabel from "@/components/ngteco-form/NGInputLabel";
import CountryService from "@/services/cis/CountryService";
import UserService from "@/services/auth/UserService";
import Switch from "@mui/material/Switch";


const SiteView = (props: any, ref: any) => {
  const {t} = useTranslation();
  const codeRegx = /^[a-zA-Z0-9\u4e00-\u9fa5 ]+$/
  const siteValidate = object({
    name: z.string({required_error: t("common.This field is required!")})
      .min(1, t("common.This field is required!"))
      .max(30, t("common.The length of field cannot exceed digits", {number: 30}))
      .regex(codeRegx, t("common.No special character allowed!")),
    // timezone: z.object({
    //   value: z.string(),
    //   name: string()
    // }, {required_error: t("common.This field is required!"), invalid_type_error: t("common.This field is required!")}),
    timezone: z.string({required_error: t("common.This field is required!")}),
    country: z.string().min(1, t("common.This field is required!")),
    dst_key: z.boolean().optional(),
  })
  type siteInput = TypeOf<typeof siteValidate>;

  // const location = useLocation()
  // const state = location.state as { id: string }
  const navigate = useNavigate();
  const [state, setState] = useState({id: ""}) // 编辑地点id值
  const [saving, setSaving] = React.useState<boolean>(false)
  const [country, setCountry] = React.useState<string>('')
  const [oldData, setOldData] = React.useState<any>()
  const [title, setTitle] = useState("")
  const siteForm = useForm<siteInput>({
    resolver: zodResolver(siteValidate),
  })
  // const timezoneList = ngteco_timezones();
  const {register, handleSubmit} = siteForm
  const [open, setOpen] = useState(false)
  const [countryList, setCountryList] = useState([])
  const [timezoneDict, setTimezoneDict] = useState([])
  const [dstKey, setDstKey] = useState<string>('')
  const [countryCode, setCountryCode] = useState<Record<string, string>>({})
  const country_code = useRef('')

  const onClose = () => {
    siteForm.reset()
    setOpen(false)
    setCountry('')
    setDstKey('')
  }

  useEffect(() => {
    CountryService.getCountryList().then((result) => {
      const countries = result.data.data?.countries
      const result_dict = countries.reduce((obj: any, country: any) => {
        obj[country.name] = country.name;
        return obj;
      }, {});
      const code_dict = countries.reduce((obj: any, country: any) => {
        obj[country.name] = country.iso31661Cd2;
        return obj;
      }, {} as Record<string, string>);
      setCountryList(result_dict)
      setCountryCode(code_dict)
    })
  }, [])

  useEffect(() => {
    if (country_code.current) {
      UserService.getTimezoneByCountry({'iso31661Cd2': country_code.current}).then((res => {
        setTimezoneDict(res.data?.data.timezone_dict)
      }))
    }
  }, [country])

  useImperativeHandle(ref, () => ({
    showDetail: async (id: string) => {
      if (id) {
        // setState({id})
        setTitle(t("common.Edit Site"))
        setTimezoneDict([])
        await GenericService.retrieve(LocationEndpoints.SITE_URI_V2, id || "").then((res) => {
          const site = res.data
          setDstKey(site.address?.city)
          const time_zone = site.address?.city !== "default" ? site.address?.city : ""
          siteForm.reset({
            name: site.name,
            timezone: time_zone,
            dst_key: site.address?.dayLightSaving,
          })
          setCountry(site?.country !== 'default' ? site?.country : '')
          country_code.current = countryCode[site?.country]
          setOldData({
            name: site.name,
            timezone: site.address?.city,
            country: site.country,
            dst_key: site.address?.dayLightSaving || false,
          })
        })
        setState({id})
      } else {
        setTitle(t("common.New Site"))
        siteForm.reset({name: '', timezone: '', country: ''})
        setCountry('')
        setState({id: ''})
        setDstKey('')
      }
      setOpen(true)
    }
  }));

  const onSave = async () => {
    setSaving(true)
    try {
      let result;
      if (state?.id) {
        const values = siteForm.getValues()
        const newSite = {
          name: values.name,
          address: {
            timeZone: values.timezone,
            city: dstKey,
            addressLine1: "default",
            country: values.country,
            dayLightSaving: values.dst_key || false,
          },
          country: values.country,
          timezone: values.timezone,
          dst_key: values.dst_key || false,
        }
        const data = {
          ...newSite,
          oldData: oldData
        }
        result = await GenericService.update(LocationEndpoints.SITE_URI_V2, state.id || '', data)
        if (result?.status < 400)
          SnackbarUtil.success(t('common.Site updated successfully.'))
        else
          SnackbarUtil.error(result?.message)
        if (props.reloadTable) {
          props.reloadTable()
        }
        onClose()
      } else {
        const values = siteForm.getValues()
        const newSite = {
          name: values.name,
          address: {
            timeZone: values.timezone,
            city: dstKey,
            addressLine1: "default",
            country: values.country,
            dayLightSaving: values.dst_key || false,
          }
        }
        result = await GenericService.create(LocationEndpoints.SITE_URI_V2, newSite)
        if (result?.status < 400)
          SnackbarUtil.success(t('common.Site added successfully.'))
        else
          SnackbarUtil.error(result?.message)
      }
      if (props.reloadTable) {
        props.reloadTable()
      }
      onClose()
    } catch (e) {
      console.log("error===", e)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
    >
      <Grid sx={{
        minWidth: '300px',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        position: 'relative',
      }}>
        <Grid sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '56px',
          padding: '0px 24px',
          borderBottom: '1px solid #F2F5F8',
        }}>
          <Grid>{title}</Grid>
          <Grid>
            <i style={{cursor: "pointer"}} className="icon iconfont icon-ic_close" onClick={onClose}></i>
          </Grid>
        </Grid>
        <Paper sx={{padding: '20px', width: '400px', boxShadow: 'none'}}>
          <Grid container>
            <Grid lg={12} item sx={{marginBottom: "8px"}}>
              <ControlledTextFiled
                hookForm={siteForm}
                label={t("common.Site Name")}
                name="name"
                fullWidth
                required
                placeholder={t("common.Site Name")}
              />
            </Grid>
          </Grid>
          <Grid container>
            <Grid lg={12} item sx={{marginBottom: "8px"}}>
              <NGInputLabel label={t("signUp.Country or Region")} required={true}/>
              <Select
                id="country-select"
                fullWidth
                required={true}
                value={country}
                {...register("country")}
                onChange={(event) => {
                  setDstKey('')
                  setCountry(event.target.value)
                  country_code.current = countryCode[event.target.value]
                  siteForm.setValue('timezone', '')
                }}
              >
                {Object.entries(countryList).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </Grid>
          <Grid container>
            <Grid lg={12} item sx={{marginBottom: "8px"}}>
              <Stack sx={{lineHeight: "5vh"}}>
                <NGInputLabel label={t("common.Site Time Zone")} required={true}/>
                <Select
                  id="select_timezone"
                  fullWidth
                  required={true}
                  value={dstKey}
                  onChange={(event) => {
                    setDstKey(event.target.value)
                    siteForm.setValue("timezone", event.target.value)
                  }}
                >
                  {Object.entries(timezoneDict).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </Stack>
            </Grid>
          </Grid>
          <Grid container>
            <Grid lg={12} item sx={{ marginBottom: "8px" }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <NGInputLabel label={t("common.Daylight Saving Time")} />
                <Controller
                  name="dst_key"
                  control={siteForm.control}
                  render={({ field }) => {
                    return (
                      <Switch
                        {...field}
                        checked={field.value ?? false}
                        onChange={(e) => field.onChange(e.target.checked)}
                        color="primary"
                      />
                    );
                  }}
                />
              </Stack>
            </Grid>
          </Grid>
        </Paper>
        <Grid sx={{borderTop: '1px solid #F2F5F8', position: 'absolute', bottom: '12px', left: '0', width: '100%'}}>
          <Grid style={{paddingTop: '12px', paddingLeft: '24px'}}>
            <Stack spacing={3} direction={"row"} justifyContent="start">
              <CancelButton onClick={onClose}/>
              <SavingButton children={t("common.confirm")} loading={saving} onClick={async () => {
                if (!siteForm.getValues('country')){
                  SnackbarUtil.error(t("organ.The country is required"), {
                    anchorOrigin: {
                      vertical: 'top',
                      horizontal: 'center'
                    }
                  });
                  return
                }
                if (!siteForm.getValues('timezone')) {
                  SnackbarUtil.error(t("organ.The TimeZone field is required"), {
                    anchorOrigin: {
                      vertical: 'top',
                      horizontal: 'center'
                    }
                  });
                  return
                }
                const isValid = await siteForm.trigger()
                if (isValid) {
                  onSave()
                }
              }}/>
            </Stack>
          </Grid>
        </Grid>
      </Grid>
    </Drawer>
  )
}

export default forwardRef(SiteView);

